import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  addDays,
  addMonths,
  differenceInDays,
  format,
  subMonths,
} from "date-fns";
import QuickSearch from "../../../components/QuickSearch";
import { CompanyDetailPage, StatusRenderer } from "./EsopAgComponents";
import {
  PrimaryCTAButton,
  SecondaryCTAButton,
} from "../../../components/CTAButtonComponents";
import { Spinner } from "../../../components/loader";
import { sideBar } from "../../../utils/AgGridConfig";
import { formatDisplayDate } from "../../../utils/formatDateDisplay";
import BillingReport from "../../billingPages/BillingReport";
import { BillingData } from "../../../types/companyStats";
import {
  AggregateEsopReport,
  AggregateEsopReportData,
  AggregateEsopReportFilters,
  getAggregateEsopReport,
  getActiveAggregateEsopReports,
  getLatestAggregateEsopReport,
  getAggregateEsopReportData,
  requestAggregateEsopReport,
} from "../../../api/esopStats";
import { useGetEsopCompanyStats } from "../../../queries/companyStats/esopStats";
import AggregateEsopReportPreview from "./AggregateEsopReportPreview";
import ActiveAggregateEsopReports from "./ActiveAggregateEsopReports";

export type EsopDetails = {
  id: string;
  companyId: string;
  companyName: string;
  dbaName: string;
  onPlatformSince: Date;
  cin: string;
  companyPoolSize: number;
  totalActivePlanPoolSize: number;
  totalNumberOfPlans: number;
  totalNumberOfEmployees: number;
  totalNumberOfResignedEmployees: number;
  numberOfEmployeesGivenAccess: number;
  totalCashedout: number;
  totalLapsed: number;
  totalStockExercise: number;
  totalSurrendered: number;
  totalVested: number;
  totalGranted: number;
  totalSold: number;
  onboardingStatus: string;
  companyStatus: string;
  adminUsers: number;
  billingData: BillingData;
};

const aggregateReportActiveStorageKey = "esop-aggregate-report:active";
const aggregateReportStorageTtlMs = 7 * 24 * 60 * 60 * 1000;
const aggregateReportStorageKey = (externalRequestId?: string) =>
  `esop-aggregate-report:${externalRequestId || "draft"}`;

function EsopStats() {
  const gridApi = useRef<AgGridReact>(null);
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [searchResultCompanies, setSearchResultCompanies] = useState(0);
  const { data: esopCompanyStats = [], isLoading: isLoadingEsopCompanies } =
    useGetEsopCompanyStats();
  const data = esopCompanyStats as EsopDetails[];
  const [isColumnOpen, setIsColumnOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isGenerateFullBillPopup, setIsGenerateFullBillPopup] = useState(false);
  const [isGenerateAggregateReportOpen, setIsGenerateAggregateReportOpen] =
    useState(false);
  const [fromDate, setFromDate] = useState(
    format(subMonths(new Date(), 12), "yyyy-MM-dd"),
  );
  const [toDate, setToDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [filters, setFilters] = useState<AggregateEsopReportFilters>(() => {
    const today = new Date();
    const historicalFrom = format(subMonths(today, 12), "yyyy-MM-dd");
    const todayValue = format(today, "yyyy-MM-dd");
    return {
      vesting: { fromDate: historicalFrom, toDate: todayValue },
      exercise: { fromDate: historicalFrom, toDate: todayValue },
      expiry: {
        fromDate: todayValue,
        toDate: format(addMonths(today, 12), "yyyy-MM-dd"),
      },
      liquidity: { fromDate: historicalFrom, toDate: todayValue },
    };
  });
  const [isReportFiltersOpen, setIsReportFiltersOpen] = useState(true);
  const [aggregateReport, setAggregateReport] =
    useState<AggregateEsopReport | null>(null);
  const [isRequestingAggregateReport, setIsRequestingAggregateReport] =
    useState(false);
  const [aggregateReportError, setAggregateReportError] = useState("");
  const [aggregateReportData, setAggregateReportData] =
    useState<AggregateEsopReportData | null>(null);
  const [isAggregateReportPreviewOpen, setIsAggregateReportPreviewOpen] =
    useState(false);
  const [latestAggregateReport, setLatestAggregateReport] =
    useState<AggregateEsopReport | null>(null);
  const [activeAggregateReports, setActiveAggregateReports] = useState<
    AggregateEsopReport[]
  >([]);
  const [isActiveAggregateReportsOpen, setIsActiveAggregateReportsOpen] =
    useState(false);
  const [isAggregateStateHydrated, setIsAggregateStateHydrated] =
    useState(false);
  const navigate = useNavigate();
  const loadActiveAggregateReports = () =>
    getActiveAggregateEsopReports()
      .then(setActiveAggregateReports)
      .catch(() => undefined);
  // const darkThemeMq = window.matchMedia("(prefers-color-scheme: dark)");
  // if (darkThemeMq.matches) {
  //   // Theme set to dark.
  // } else {
  //   // Theme set to light.
  // }
  // window
  //   .matchMedia("(prefers-color-scheme: dark)")
  //   .addEventListener("change", (e) => {
  //     setIsDarkTheme(e.matches);
  //     console.log(e);
  //   });

  useEffect(() => {
    let active = true;
    getLatestAggregateEsopReport()
      .then((report) => {
        if (active) setLatestAggregateReport(report);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    loadActiveAggregateReports();
  }, []);
  useEffect(() => {
    setSearchResultCompanies(
      gridApi?.current?.api?.getDisplayedRowCount() || 0,
    );
  }, [gridApi?.current?.api]);
  useEffect(() => {
    if (
      !aggregateReport?.externalRequestId ||
      aggregateReport.status === "COMPLETED" ||
      aggregateReport.status === "FAILED"
    ) {
      return undefined;
    }
    let active = true;
    const refresh = async () => {
      try {
        const next = await getAggregateEsopReport(
          aggregateReport.externalRequestId,
        );
        if (active) setAggregateReport(next);
      } catch {
        if (active) {
          setAggregateReportError(
            "Unable to refresh the report status. Please try again.",
          );
        }
      }
    };
    refresh().catch(() => undefined);
    const timer = window.setInterval(() => {
      refresh().catch(() => undefined);
    }, 5_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [aggregateReport?.externalRequestId, aggregateReport?.status]);
  useEffect(() => {
    if (
      !aggregateReport?.externalRequestId ||
      aggregateReport.status !== "COMPLETED" ||
      aggregateReportData
    ) {
      return () => undefined;
    }
    let active = true;
    getAggregateEsopReportData(aggregateReport.externalRequestId)
      .then((snapshot) => {
        if (active) setAggregateReportData(snapshot);
      })
      .catch(() => {
        if (active)
          setAggregateReportError(
            "The report is ready, but its data could not be loaded. Please try again.",
          );
      });
    return () => {
      active = false;
    };
  }, [
    aggregateReport?.externalRequestId,
    aggregateReport?.status,
    aggregateReportData,
  ]);
  useEffect(() => {
    if (
      aggregateReport?.status === "COMPLETED" &&
      aggregateReport.downloadUrl
    ) {
      setLatestAggregateReport(aggregateReport);
      loadActiveAggregateReports();
    }
  }, [aggregateReport]);
  useEffect(() => {
    try {
      const activeReportId = window.localStorage.getItem(
        aggregateReportActiveStorageKey,
      );
      const stored = window.localStorage.getItem(
        aggregateReportStorageKey(activeReportId || undefined),
      );
      if (!stored) return;
      const saved = JSON.parse(stored) as {
        fromDate?: string;
        toDate?: string;
        filters?: AggregateEsopReportFilters;
        aggregateReport?: AggregateEsopReport;
        aggregateReportData?: AggregateEsopReportData;
        savedAt?: number;
      };
      if (
        !saved.savedAt ||
        Date.now() - saved.savedAt > aggregateReportStorageTtlMs
      ) {
        window.localStorage.removeItem(
          aggregateReportStorageKey(activeReportId || undefined),
        );
        window.localStorage.removeItem(aggregateReportActiveStorageKey);
        return;
      }
      if (saved.fromDate) setFromDate(saved.fromDate);
      if (saved.toDate) setToDate(saved.toDate);
      if (saved.filters) setFilters(saved.filters);
      if (saved.aggregateReport) setAggregateReport(saved.aggregateReport);
      if (saved.aggregateReportData)
        setAggregateReportData(saved.aggregateReportData);
    } catch {
      window.localStorage.removeItem(aggregateReportActiveStorageKey);
    } finally {
      setIsAggregateStateHydrated(true);
    }
  }, []);
  useEffect(() => {
    if (!isAggregateStateHydrated) return;
    const reportId = aggregateReport?.externalRequestId;
    window.localStorage.setItem(
      aggregateReportStorageKey(reportId),
      JSON.stringify({
        fromDate,
        toDate,
        filters,
        aggregateReport,
        aggregateReportData,
        savedAt: Date.now(),
      }),
    );
    if (reportId)
      window.localStorage.setItem(aggregateReportActiveStorageKey, reportId);
  }, [
    fromDate,
    toDate,
    filters,
    aggregateReport,
    aggregateReportData,
    isAggregateStateHydrated,
  ]);

  const generateAggregateReport = async (forceRefresh = false) => {
    if (!fromDate || !toDate || fromDate > toDate) {
      setAggregateReportError(
        "Choose a valid report date range where From date is before To date.",
      );
      return;
    }
    if (
      Object.values(filters).some(
        (range) =>
          !range.fromDate || !range.toDate || range.fromDate > range.toDate,
      )
    ) {
      setAggregateReportError(
        "Each state filter needs a valid From date and To date.",
      );
      return;
    }
    try {
      setIsRequestingAggregateReport(true);
      setAggregateReportError("");
      setIsAggregateReportPreviewOpen(false);
      setAggregateReportData(null);
      setAggregateReport(
        await requestAggregateEsopReport({
          fromDate,
          toDate,
          filters,
          ...(forceRefresh ? { forceRefresh: true } : {}),
        }),
      );
    } catch {
      setAggregateReportError("Unable to queue the aggregate ESOP report.");
    } finally {
      setIsRequestingAggregateReport(false);
    }
  };
  const clearActiveAggregateReport = () => {
    const reportId = aggregateReport?.externalRequestId;
    if (reportId)
      window.localStorage.removeItem(aggregateReportStorageKey(reportId));
    window.localStorage.removeItem(aggregateReportActiveStorageKey);
    setIsAggregateReportPreviewOpen(false);
    setAggregateReportData(null);
    setAggregateReport(null);
  };
  const updateReportFilter = (
    filter: keyof AggregateEsopReportFilters,
    field: "fromDate" | "toDate",
    value: string,
  ) => {
    setFilters((current) => ({
      ...current,
      [filter]: { ...current[filter], [field]: value },
    }));
  };
  const useOverallPeriod = (filter: keyof AggregateEsopReportFilters) => {
    setFilters((current) => ({
      ...current,
      [filter]: { fromDate, toDate },
    }));
  };
  const relativePeriodRange = (
    direction: "past" | "future",
    months: number,
  ) => {
    const reportEnd = new Date(`${toDate}T00:00:00`);
    return direction === "past"
      ? {
          fromDate: format(subMonths(reportEnd, months), "yyyy-MM-dd"),
          toDate,
        }
      : {
          fromDate: format(addDays(reportEnd, 1), "yyyy-MM-dd"),
          toDate: format(addMonths(reportEnd, months), "yyyy-MM-dd"),
        };
  };
  const displayReportDate = (value: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value || "—";
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? value : format(date, "dd-MMM-yyyy");
  };
  const hasRange = (
    filter: keyof AggregateEsopReportFilters,
    range: { fromDate: string; toDate: string },
  ) =>
    filters[filter].fromDate === range.fromDate &&
    filters[filter].toDate === range.toDate;
  const applyRelativePeriod = (
    filter: keyof AggregateEsopReportFilters,
    direction: "past" | "future",
    months: number,
  ) => {
    const range = relativePeriodRange(direction, months);
    setFilters((current) => ({ ...current, [filter]: range }));
  };
  const isCurrentReportConfiguration = Boolean(
    aggregateReport &&
      aggregateReport.fromDate === fromDate &&
      aggregateReport.toDate === toDate &&
      aggregateReport.filters &&
      JSON.stringify(aggregateReport.filters) === JSON.stringify(filters),
  );
  const previewAvailableAggregateReport = async (
    report: AggregateEsopReport,
  ) => {
    try {
      setAggregateReportError("");
      setAggregateReportData(
        await getAggregateEsopReportData(report.externalRequestId),
      );
      setIsActiveAggregateReportsOpen(false);
      setIsAggregateReportPreviewOpen(true);
    } catch {
      setAggregateReportError(
        "This report preview is no longer available. Download the saved report instead.",
      );
    }
  };
  const components = useMemo(
    () => ({
      companyName: CompanyDetailPage,
      onboarding_status: StatusRenderer,
    }),
    [],
  );
  const legacyRowsData = useMemo(() => {
    const compData = [...data].sort((a: any, b: any) =>
      a.companyName.localeCompare(b.companyName),
    );
    let finalData: any[] = [];
    const newComp: any[] = [];
    compData?.forEach((i: EsopDetails) => {
      const days = differenceInDays(new Date(), new Date(i.onPlatformSince));
      if (days <= 30) {
        newComp.push({
          ...i,
          companyId: i.companyId,
          companyName: i.companyName,
          dbaName: i.dbaName,
          onPlatformSince: formatDisplayDate(i.onPlatformSince),
          cin: i.cin,
          companyPoolSize: Intl.NumberFormat("en-IN").format(i.companyPoolSize),
          totalActivePlanPoolSize: Intl.NumberFormat("en-IN").format(
            i.totalActivePlanPoolSize,
          ),
          totalNumberOfPlans: i.totalNumberOfPlans,
          totalNumberOfEmployees: i.totalNumberOfEmployees,
          numberOfEmployeesGivenAccess: i.numberOfEmployeesGivenAccess,
          totalNumberOfResignedEmployees: i.totalNumberOfResignedEmployees,
          totalCashedout: Intl.NumberFormat("en-IN").format(i.totalCashedout),
          totalLapsed: Intl.NumberFormat("en-IN").format(i.totalLapsed),
          totalStockExercise: Intl.NumberFormat("en-IN").format(
            i.totalStockExercise,
          ),
          totalSurrendered: Intl.NumberFormat("en-IN").format(
            i.totalSurrendered,
          ),
          totalVested: Intl.NumberFormat("en-IN").format(i.totalVested),
          totalGranted: Intl.NumberFormat("en-IN").format(i.totalGranted),
          totalSold: Intl.NumberFormat("en-IN").format(i.totalSold),
          onboarding_status: i.onboardingStatus
            .replaceAll("_", " ")
            .toLowerCase(),
          company_status: i.companyStatus.replaceAll("_", " ").toLowerCase(),
          companyOnboardedDate: i.billingData.companyOnboardedDate
            ? formatDisplayDate(i.billingData.companyOnboardedDate)
            : "-",
          companyGoLiveDate: i.billingData.companyGoLiveDate
            ? formatDisplayDate(i.billingData.companyGoLiveDate)
            : "-",
          isHissaCustomer: i.billingData.isHissaCustomer ?? false,
        });
      } else {
        finalData.push({
          ...i,
          companyId: i.companyId,
          companyName: i.companyName,
          dbaName: i.dbaName,
          onPlatformSince: formatDisplayDate(i.onPlatformSince),
          cin: i.cin,
          companyPoolSize: Intl.NumberFormat("en-IN").format(i.companyPoolSize),
          totalActivePlanPoolSize: Intl.NumberFormat("en-IN").format(
            i.totalActivePlanPoolSize,
          ),
          totalNumberOfPlans: i.totalNumberOfPlans,
          totalNumberOfEmployees: i.totalNumberOfEmployees,
          numberOfEmployeesGivenAccess: i.numberOfEmployeesGivenAccess,
          totalNumberOfResignedEmployees: i.totalNumberOfResignedEmployees,
          totalCashedout: Intl.NumberFormat("en-IN").format(i.totalCashedout),
          totalLapsed: Intl.NumberFormat("en-IN").format(i.totalLapsed),
          totalStockExercise: Intl.NumberFormat("en-IN").format(
            i.totalStockExercise,
          ),
          totalSurrendered: Intl.NumberFormat("en-IN").format(
            i.totalSurrendered,
          ),
          totalVested: Intl.NumberFormat("en-IN").format(i.totalVested),
          totalGranted: Intl.NumberFormat("en-IN").format(i.totalGranted),
          totalSold: Intl.NumberFormat("en-IN").format(i.totalSold),
          onboarding_status: i.onboardingStatus
            .replaceAll("_", " ")
            .toLowerCase(),
          company_status: i.companyStatus.replaceAll("_", " ").toLowerCase(),
          companyOnboardedDate: i.billingData.companyOnboardedDate
            ? formatDisplayDate(i.billingData.companyOnboardedDate)
            : "-",
          companyGoLiveDate: i.billingData.companyGoLiveDate
            ? formatDisplayDate(i.billingData.companyGoLiveDate)
            : "-",
          isHissaCustomer: i.billingData.isHissaCustomer ?? false,
        });
      }
    });
    finalData = [...newComp, ...finalData];
    return finalData;
  }, [data]);
  const legacyColumnDefs: ColDef[] = [
    {
      headerName: "Company Name",
      field: "companyName",
      cellRenderer: "companyName",
      filter: "agTextColumnFilter",
      // sort: "asc",
    },
    {
      headerName: "DBA Name",
      field: "dbaName",
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Onboarding Status",
      field: "onboarding_status",
      cellRenderer: "onboarding_status",
      minWidth: 200,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Company Status",
      field: "company_status",
      filter: "agTextColumnFilter",
      cellClass: "capitalize",
    },
    {
      headerName: "Admin Users",
      field: "adminUsers",
    },
    {
      headerName: "Company Pool Size",
      field: "companyPoolSize",
      cellStyle: { display: "flex", justifyContent: "center" },
    },
    {
      headerName: "Total Active Plan Pool Size",
      field: "totalActivePlanPoolSize",
      cellStyle: {
        display: "flex",
        justifyContent: "center",
      },
    },
    {
      headerName: "Total Number of Plans",
      field: "totalNumberOfPlans",
      cellStyle: { display: "flex ", justifyContent: "center" },
    },
    {
      headerName: "Total Number of Employees",
      field: "totalNumberOfEmployees",
      cellStyle: { display: "flex ", justifyContent: "center" },
    },
    {
      headerName: "Company Onboarded Date",
      field: "companyOnboardedDate",
      filter: "agDateColumnFilter",
      minWidth: 180,
    },
    {
      headerName: "Company Go Live Date",
      field: "companyGoLiveDate",
      filter: "agDateColumnFilter",
      minWidth: 180,
    },
    {
      headerName: "Hissa Customer",
      field: "isHissaCustomer",
      filter: "agTextColumnFilter",
      cellRenderer: (params: any) => {
        return params.value ? "Yes" : "No";
      },
      minWidth: 150,
    },
  ];
  const rowsData = legacyRowsData;
  const columnDefs = legacyColumnDefs;
  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      filter: true,
      minWidth: 150,
      flex: 1,
    }),
    [],
  );
  const openToolPanel = (key: any) => {
    if (key === "columns") {
      if (gridApi) {
        if (!isColumnOpen) gridApi?.current?.api?.openToolPanel(key);
        else gridApi?.current?.api?.closeToolPanel();
        setIsColumnOpen((state) => !state);
        setIsFilterOpen(false);
      }
    } else if (key === "filters") {
      if (gridApi) {
        if (!isFilterOpen) {
          gridApi?.current?.api?.openToolPanel(key);
        } else gridApi?.current?.api?.closeToolPanel();
        setIsFilterOpen((state) => !state);
        setIsColumnOpen(false);
      }
    }
  };
  return (
    <>
      <div className="flex justify-between">
        <div>
          <h1 className="text-2xl font-medium">ESOP Companies</h1>
          <p className="pb-2 text-sm font-medium text-gray-500">
            {searchResultCompanies} companies
          </p>
        </div>
        <div className="flex py-2">
          <QuickSearch
            placeholder="Search"
            onChange={(e) => {
              gridApi.current!.api.setGridOption(
                "quickFilterText",
                e.target.value,
              );
              setSearchResultCompanies(
                gridApi.current?.api.getDisplayedRowCount() || 0,
              );
            }}
          />
        </div>
      </div>
      <div>
        <div className="flex justify-between gap-2 py-2">
          <div>
            {/* {latestAggregateReport?.downloadUrl && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>
                  Last report generated with date:{" "}
                  {latestAggregateReport.generatedAt
                    ? format(
                        new Date(latestAggregateReport.generatedAt),
                        "dd-MMM-yyyy HH:mm",
                      )
                    : "—"}
                </span>
                <SecondaryCTAButton
                  title={`Available until ${latestAggregateReport.expiresAt || "expiry"}`}
                  onClick={() =>
                    window.open(
                      latestAggregateReport.downloadUrl || undefined,
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                >
                  Download
                </SecondaryCTAButton>
              </div>
            )} */}
            <SecondaryCTAButton
              onClick={() => {
                loadActiveAggregateReports();
                setIsActiveAggregateReportsOpen(true);
              }}
            >
              Available reports
            </SecondaryCTAButton>
          </div>
          <div className="flex justify-end gap-2">
            <PrimaryCTAButton
              onClick={() => {
                setIsGenerateFullBillPopup(!isGenerateFullBillPopup);
              }}
            >
              Generate Invoice
            </PrimaryCTAButton>
            <PrimaryCTAButton
              onClick={() => {
                setAggregateReportError("");
                setIsGenerateAggregateReportOpen(true);
              }}
            >
              Generate ESOP Report
            </PrimaryCTAButton>
            <PrimaryCTAButton
              onClick={() => {
                openToolPanel("columns");
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="currentColor"
                className="size-4 me-1"
              >
                <path d="M9.836 3h-3.67v10h3.67V3ZM11.336 13H13.5a1.5 1.5 0 0 0 1.5-1.5v-7A1.5 1.5 0 0 0 13.5 3h-2.164v10ZM2.5 3h2.166v10H2.5A1.5 1.5 0 0 1 1 11.5v-7A1.5 1.5 0 0 1 2.5 3Z" />
              </svg>
              Columns
            </PrimaryCTAButton>
            <PrimaryCTAButton
              onClick={() => {
                openToolPanel("filters");
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="currentColor"
                className="size-4 me-1"
              >
                <path d="M14 2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v2.172a2 2 0 0 0 .586 1.414l2.828 2.828A2 2 0 0 1 6 9.828v4.363a.5.5 0 0 0 .724.447l2.17-1.085A2 2 0 0 0 10 11.763V9.829a2 2 0 0 1 .586-1.414l2.828-2.828A2 2 0 0 0 14 4.172V2Z" />
              </svg>
              Filter
            </PrimaryCTAButton>
          </div>
          {isGenerateFullBillPopup && (
            <BillingReport
              isShow={isGenerateFullBillPopup}
              companyId={""}
              onClose={() => {
                setIsGenerateFullBillPopup(!isGenerateFullBillPopup);
              }}
            />
          )}
          {isGenerateAggregateReportOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
              <div className="flex max-h-[75vh] w-full max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-lg bg-white shadow-xl sm:max-w-5xl">
                <div className="relative shrink-0 border-b border-gray-200 px-6 py-4">
                  <button
                    aria-label="Close ESOP report dialog"
                    className="absolute right-4 top-4 rounded p-1 text-xl leading-none text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                    onClick={() => setIsGenerateAggregateReportOpen(false)}
                    type="button"
                  >
                    ×
                  </button>
                  <h2 className="pr-8 text-lg font-semibold">
                    Create ESOP report
                  </h2>
                  <p className="mt-1 pr-8 text-sm text-gray-500">
                    Choose an overall report period, then independently set the
                    date range used for each option state.
                  </p>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                  <div className="mt-5 rounded-md border border-gray-200 bg-gray-50 p-4">
                    <p className="text-sm font-medium text-gray-700">
                      Report period
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      The To date is used for the current option and share-value
                      snapshot.
                    </p>
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <label className="text-sm font-medium text-gray-700">
                        From date <span className="text-red-500">*</span>
                        <input
                          className="mt-1 w-full rounded border border-gray-300 bg-white p-2"
                          type="date"
                          required
                          value={fromDate}
                          max={toDate}
                          onChange={(event) => setFromDate(event.target.value)}
                        />
                      </label>
                      <label className="text-sm font-medium text-gray-700">
                        To date <span className="text-red-500">*</span>
                        <input
                          className="mt-1 w-full rounded border border-gray-300 bg-white p-2"
                          type="date"
                          required
                          value={toDate}
                          min={fromDate}
                          onChange={(event) => setToDate(event.target.value)}
                        />
                      </label>
                    </div>
                  </div>
                  <p className="mt-4 text-sm font-medium text-gray-700">
                    What would you like to include?
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Set a date range for each part of the report. You can look
                    at past activity and future vesting or expiry together.
                    Fields marked <span className="text-red-500">*</span> are
                    required.
                  </p>
                  <div className="mt-3 grid gap-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600 sm:grid-cols-3">
                    <p>
                      <span className="font-semibold text-gray-800">
                        Already vested
                      </span>
                      <br />
                      Outstanding vested options as at the report To date.
                    </p>
                    <p>
                      <span className="font-semibold text-gray-800">
                        To be vested
                      </span>
                      <br />
                      Projected vesting from the selected vesting range.
                    </p>
                    <p>
                      <span className="font-semibold text-gray-800">
                        Exercise history
                      </span>
                      <br />
                      Completed exercises only; future exercise is not assumed.
                    </p>
                  </div>
                  <button
                    aria-expanded={isReportFiltersOpen}
                    className="mt-4 flex w-full items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-left text-sm font-medium text-gray-700 hover:bg-gray-100"
                    onClick={() =>
                      setIsReportFiltersOpen((current) => !current)
                    }
                    type="button"
                  >
                    <span>Filter timelines</span>
                    <span aria-hidden="true" className="text-lg leading-none">
                      {isReportFiltersOpen ? "⌃" : "⌄"}
                    </span>
                  </button>
                  {isReportFiltersOpen && (
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      {[
                        {
                          key: "vesting" as const,
                          label: "Vesting activity and forecast",
                          description:
                            "Completed vesting and projected vesting in this range.",
                          example:
                            "Example: select 01 Apr 2026 to 31 Mar 2027 to see vesting completed and scheduled in that period. Already vested options remain an as-of snapshot on the report To date.",
                        },
                        {
                          key: "exercise" as const,
                          label: "Exercise history",
                          description:
                            "Completed stock-exercise transactions only.",
                          example:
                            "Example: select the last 6 months to include only exercises completed during those 6 months. Future exercise is not assumed.",
                        },
                        {
                          key: "expiry" as const,
                          label: "Expiry",
                          description:
                            "Vested options whose expiry date falls in this range.",
                          example:
                            "Example: select the next 12 months to identify vested, unexercised options that may expire in that period.",
                        },
                        {
                          key: "liquidity" as const,
                          label: "Liquidity",
                          description: "Completed cash and liquidity events.",
                          example:
                            "Example: select a financial year to total completed liquidity delivered during that year.",
                        },
                      ].map(({ key, label, description, example }) => {
                        const filter = key;
                        const range = filters[filter];
                        return (
                          <div
                            key={key}
                            className="rounded-md border border-gray-200 p-4 text-sm font-medium text-gray-700"
                          >
                            <div className="flex w-full items-center justify-between gap-3">
                              <span className="flex items-center gap-1.5">
                                {label}
                                <span
                                  aria-label={`${label} example`}
                                  className="inline-flex size-4 cursor-help items-center justify-center rounded-full border border-gray-400 text-[10px] font-semibold text-gray-600"
                                  title={example}
                                >
                                  i
                                </span>
                              </span>
                              <span className="rounded border border-gray-200 bg-gray-100 px-2 py-1 text-xs font-normal text-gray-600">
                                {displayReportDate(range.fromDate)} →{" "}
                                {displayReportDate(range.toDate)}
                              </span>
                            </div>
                            <>
                              <span className="mt-2 block text-xs font-normal leading-4 text-gray-500">
                                {description}
                              </span>
                              <button
                                className={`mt-3 rounded border px-2.5 py-1.5 text-xs font-medium ${
                                  hasRange(filter, { fromDate, toDate })
                                    ? "border-gray-400 bg-gray-100 text-gray-800"
                                    : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                                }`}
                                type="button"
                                onClick={() => useOverallPeriod(filter)}
                              >
                                Set to report period
                              </button>
                              <div className="mt-2 flex flex-wrap gap-2">
                                {filter === "exercise" ||
                                filter === "liquidity" ? (
                                  <>
                                    {[3, 6, 12].map((months) => (
                                      <button
                                        key={`past-${months}`}
                                        className={`rounded border px-2 py-1 text-xs font-medium ${
                                          hasRange(
                                            filter,
                                            relativePeriodRange("past", months),
                                          )
                                            ? "border-gray-400 bg-gray-100 text-gray-800"
                                            : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                                        }`}
                                        type="button"
                                        onClick={() =>
                                          applyRelativePeriod(
                                            filter,
                                            "past",
                                            months,
                                          )
                                        }
                                      >
                                        Last {months}M
                                      </button>
                                    ))}
                                  </>
                                ) : (
                                  <>
                                    {[3, 6, 12].map((months) => (
                                      <button
                                        key={`future-${months}`}
                                        className={`rounded border px-2 py-1 text-xs font-medium ${
                                          hasRange(
                                            filter,
                                            relativePeriodRange(
                                              "future",
                                              months,
                                            ),
                                          )
                                            ? "border-gray-400 bg-gray-100 text-gray-800"
                                            : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                                        }`}
                                        type="button"
                                        onClick={() =>
                                          applyRelativePeriod(
                                            filter,
                                            "future",
                                            months,
                                          )
                                        }
                                      >
                                        Next {months}M
                                      </button>
                                    ))}
                                  </>
                                )}
                                <span className="self-center text-xs font-normal text-gray-500">
                                  Quick range
                                </span>
                              </div>
                              <div className="mt-2 grid grid-cols-2 gap-2">
                                <label className="text-xs font-normal text-gray-600">
                                  From <span className="text-red-500">*</span>
                                  <input
                                    className="mt-1 w-full rounded border border-gray-300 p-2 text-sm"
                                    type="date"
                                    required
                                    value={range.fromDate}
                                    max={range.toDate}
                                    onChange={(event) =>
                                      updateReportFilter(
                                        filter,
                                        "fromDate",
                                        event.target.value,
                                      )
                                    }
                                  />
                                </label>
                                <label className="text-xs font-normal text-gray-600">
                                  To <span className="text-red-500">*</span>
                                  <input
                                    className="mt-1 w-full rounded border border-gray-300 p-2 text-sm"
                                    type="date"
                                    required
                                    value={range.toDate}
                                    min={range.fromDate}
                                    onChange={(event) =>
                                      updateReportFilter(
                                        filter,
                                        "toDate",
                                        event.target.value,
                                      )
                                    }
                                  />
                                </label>
                              </div>
                            </>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <p className="mt-3 text-xs text-gray-500">
                    Quick ranges always resolve to the concrete dates shown in
                    each card. You can edit either date for a custom range.
                  </p>
                  {aggregateReportError && (
                    <p className="mt-3 text-sm text-red-600">
                      {aggregateReportError}
                    </p>
                  )}
                  {aggregateReport &&
                    (aggregateReport.status !== "COMPLETED" ||
                      isCurrentReportConfiguration) && (
                      <div className="mt-4 rounded bg-gray-50 p-3 text-sm">
                        {aggregateReport.status === "COMPLETED" ? (
                          <>
                            <p>
                              Report ready for{" "}
                              {aggregateReport.companyCount ?? 0} companies.
                            </p>
                            {isCurrentReportConfiguration ? (
                              <div className="mt-3 flex flex-wrap items-center gap-2 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                                <span>{`These timelines match this report. If data changed after ${
                                  aggregateReport.fetchedAt
                                    ? format(
                                        new Date(aggregateReport.fetchedAt),
                                        "dd-MMM-yyyy HH:mm",
                                      )
                                    : "it was generated"
                                }, you can regenerate it with the latest data.`}</span>
                                <SecondaryCTAButton
                                  disabled={isRequestingAggregateReport}
                                  onClick={() => generateAggregateReport(true)}
                                >
                                  Regenerate with latest data
                                </SecondaryCTAButton>
                              </div>
                            ) : null}
                            {isCurrentReportConfiguration && (
                              <>
                                {aggregateReport.downloadUrl && (
                                  <a
                                    className="mt-2 inline-block font-medium text-blue-600 underline"
                                    href={aggregateReport.downloadUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={clearActiveAggregateReport}
                                  >
                                    Download report
                                  </a>
                                )}
                                {aggregateReportData && (
                                  <button
                                    className="ml-4 mt-2 inline-block font-medium text-gray-700 underline"
                                    type="button"
                                    onClick={() => {
                                      setIsAggregateReportPreviewOpen(true);
                                      setIsGenerateAggregateReportOpen(false);
                                    }}
                                  >
                                    Preview report
                                  </button>
                                )}
                              </>
                            )}
                          </>
                        ) : aggregateReport.status === "FAILED" ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-red-600">
                              {aggregateReport.failureReason ||
                                "Report generation failed. Please generate it again."}
                            </p>
                            <SecondaryCTAButton
                              disabled={isRequestingAggregateReport}
                              onClick={() => generateAggregateReport(true)}
                            >
                              Try again with latest data
                            </SecondaryCTAButton>
                          </div>
                        ) : (
                          <p>
                            Report requested. It will update automatically when
                            ready.
                          </p>
                        )}
                      </div>
                    )}
                </div>
                <div className="flex shrink-0 justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
                  <button
                    className="rounded border border-gray-300 px-4 py-2 text-sm"
                    onClick={() => setIsGenerateAggregateReportOpen(false)}
                    type="button"
                  >
                    Close
                  </button>
                  <PrimaryCTAButton
                    onClick={() => generateAggregateReport()}
                    disabled={isRequestingAggregateReport}
                  >
                    {isRequestingAggregateReport
                      ? "Requesting..."
                      : "Generate report"}
                  </PrimaryCTAButton>
                </div>
              </div>
            </div>
          )}
        </div>
        <div
          className={`${isDarkTheme ? "ag-theme-quartz-dark" : "ag-theme-quartz"} h-[calc(100vh-28vh)]`}
        >
          {isLoadingEsopCompanies ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-sm font-medium text-gray-500">
              <Spinner />
              Loading ESOP companies…
            </div>
          ) : (
            <AgGridReact
              alwaysMultiSort
              ref={gridApi}
              components={components}
              defaultColDef={defaultColDef}
              columnDefs={columnDefs}
              rowData={rowsData}
              suppressRowTransform={true}
              pagination={true}
              animateRows={true}
              // sideBar={agConfigSideBar}
              tooltipInteraction={true}
              sideBar={sideBar}
              tooltipShowDelay={50}
              onModelUpdated={() => {
                setSearchResultCompanies(
                  gridApi.current?.api.getDisplayedRowCount() || 0,
                );
              }}
              rowStyle={{
                cursor: "pointer",
              }}
              onRowClicked={(e) => {
                navigate("/companyDetails", { state: e.data });
              }}
              getRowStyle={(params) => {
                if (params.rowIndex % 2 === 0) {
                  return { background: "#f8f8f8" };
                } else {
                  return { background: "#ffffff" };
                }
              }}
            />
          )}
        </div>
      </div>
      {isAggregateReportPreviewOpen && aggregateReportData && (
        <AggregateEsopReportPreview
          report={aggregateReportData}
          onClose={() => setIsAggregateReportPreviewOpen(false)}
        />
      )}
      {isActiveAggregateReportsOpen && (
        <ActiveAggregateEsopReports
          reports={activeAggregateReports}
          onClose={() => setIsActiveAggregateReportsOpen(false)}
          onPreview={previewAvailableAggregateReport}
        />
      )}
    </>
  );
}

export default EsopStats;
